


























































































































































import { Component, Vue } from 'vue-property-decorator'
import PageHead from '@/components/PageHead.vue'
import { Notify } from '@/helpers/Notify'
import { Api } from '@/fetch/Api'
import type { JsEmbed, CookiePolicyLink } from '@/interfaces'

interface CookiePolicyLinkRow {
  _key: number;
  id: number | null;
  url: string;
  link_text: string;
}

@Component({
  components: { PageHead },
})
export default class JsEmbedsView extends Vue {
  pageTitle = 'JS Embeds'
  snippet = ''
  includeInHead = false
  embeds: JsEmbed[] = []
  saveInProgress = false
  deleteInProgress = false
  loadInProgress = false
  cookiePolicyLinks: CookiePolicyLinkRow[] = []
  urlSaveInProgress = false
  private nextRowKey = 0

  get hasPolicydiary(): boolean {
    return this.embeds.some(embed => embed.snippet.toLowerCase().includes('policydiary.co.uk'))
  }

  mounted() {
    this.loadEmbeds()
    this.loadCookiePolicyLinks()
  }

  async loadEmbeds() {
    this.loadInProgress = true
    try {
      const embedsResponse = await Api.getJsEmbeds()
      this.embeds = embedsResponse.data || []
    } catch (error) {
      Notify.create({ type: 'error', title: 'Error', text: error.message || 'Failed to load snippets.' })
    } finally {
      this.loadInProgress = false
    }
  }

  blankRow(): CookiePolicyLinkRow {
    return { _key: this.nextRowKey++, id: null, url: '', link_text: 'GDPR & Cookie Policies' }
  }

  async loadCookiePolicyLinks() {
    try {
      const response = await Api.getCookiePolicyLinks()
      const rows = (response.data || []).map(l => ({
        _key: this.nextRowKey++,
        id: l.id ?? null,
        url: l.url,
        link_text: l.link_text,
      }))
      this.cookiePolicyLinks = rows.length > 0 ? rows : [this.blankRow()]
    } catch (error) {
      Notify.create({ type: 'error', title: 'Error', text: error.message || 'Failed to load cookie policy links.' })
      this.cookiePolicyLinks = [this.blankRow()]
    }
  }

  addRow() {
    this.cookiePolicyLinks.push(this.blankRow())
  }

  deleteRow(key: number) {
    if (this.cookiePolicyLinks.length <= 1) return
    this.cookiePolicyLinks = this.cookiePolicyLinks.filter(r => r._key !== key)
  }

  validateRows(rows: CookiePolicyLinkRow[]): string | null {
    for (const row of rows) {
      if (!row.url.trim() || !row.link_text.trim()) {
        return 'Each row must have both a URL and Link Text.'
      }
      if (row.link_text.length > 100) {
        return 'Link Text must be 100 characters or fewer.'
      }
      try {
        // eslint-disable-next-line no-new
        new URL(row.url.trim())
      } catch {
        return `"${row.url}" is not a valid URL.`
      }
    }
    return null
  }

  async submitForm() {
    if (!this.snippet.trim()) return
    this.saveInProgress = true
    try {
      await Api.saveJsEmbed(this.snippet.trim(), this.includeInHead)
      this.snippet = ''
      this.includeInHead = false
      Notify.create({ type: 'success', title: 'Saved!', text: 'JS embed snippet saved successfully.' })
      await this.loadEmbeds()
    } catch (error) {
      Notify.create({ type: 'error', title: 'Error', text: error.message || 'Failed to save snippet.' })
    } finally {
      this.saveInProgress = false
    }
  }

  async updateEmbedPlacement(id: number, newValue: boolean) {
    try {
      await Api.updateJsEmbed(id, newValue)
      const embed = this.embeds.find(e => e.id === id)
      if (embed) {
        embed.include_in_head = newValue
      }
      Notify.create({ type: 'success', title: 'Updated!', text: 'JS embed placement updated successfully.' })
    } catch (error) {
      Notify.create({ type: 'error', title: 'Error', text: error.message || 'Failed to update placement.' })
      await this.loadEmbeds()
    }
  }

  async deleteEmbed(id: number) {
    this.deleteInProgress = true
    try {
      await Api.deleteJsEmbed(id)
      Notify.create({ type: 'success', title: 'Deleted!', text: 'JS embed snippet deleted.' })
      await this.loadEmbeds()
      await this.loadCookiePolicyLinks()
    } catch (error) {
      Notify.create({ type: 'error', title: 'Error', text: error.message || 'Failed to delete snippet.' })
    } finally {
      this.deleteInProgress = false
    }
  }

  async saveCustomUrl() {
    const rowsToSave = this.cookiePolicyLinks.filter(r => r.url.trim() || r.link_text.trim())

    if (rowsToSave.length > 0) {
      const error = this.validateRows(rowsToSave)
      if (error) {
        Notify.create({ type: 'error', title: 'Invalid input', text: error })
        return
      }
    }

    this.urlSaveInProgress = true
    try {
      const payload: CookiePolicyLink[] = rowsToSave.map(r => ({ url: r.url.trim(), link_text: r.link_text.trim() }))
      await Api.saveCookiePolicyLinks(payload)
      Notify.create({ type: 'success', title: 'Saved!', text: 'Custom policy links saved successfully.' })
      await this.loadCookiePolicyLinks()
    } catch (error) {
      Notify.create({ type: 'error', title: 'Error', text: error.message || 'Failed to save custom links.' })
    } finally {
      this.urlSaveInProgress = false
    }
  }
}
